import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useId, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import Button from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { getLotHistoryDescription } from "@/components/ui/lot-number-details";
import { LotRowLabel } from "@/components/ui/lot-number-select";
import { DynamicSelectItem } from "@/components/ui/select";
import { cx } from "@/lib/tailwind-utils";
import { type LotNumberHistory, getLotNumbers } from "@/api/provider/scribe/notes";
import { ShipmentLot } from "@/query/medications/medications";
import { useTenantStore } from "@/stores/tenant-store";
export function preventDoseLotEntryDismiss(event: KeyboardEvent) {
  if (document.activeElement instanceof HTMLInputElement && document.activeElement.closest("[data-dose-lot-new-entry]")) event.preventDefault();
}

/** Keep the existing form encoding private; the editor always works with a collection. */
export function parseDoseLotReferences(value?: string): string[] {
  const codes = (value ?? "").split("\n").map(code => code.trim()).filter(Boolean);
  return Array.from(new Map(codes.map(code => [code.toLowerCase(), code])).values());
}
export function useDoseLotHistory({
  medicationId,
  locationId,
  isLoadingInventory = false
}: {
  medicationId?: string | null;
  locationId?: string | null;
  isLoadingInventory?: boolean;
}) {
  const tenant = useTenantStore(state => state.currentTenant);
  return useQuery({
    queryKey: ["lot-numbers", tenant, medicationId ?? null, locationId ?? null],
    queryFn: async () => (await getLotNumbers({
      medicationId: medicationId ?? undefined,
      locationId: locationId ?? undefined
    })) ?? [],
    enabled: !!medicationId && !isLoadingInventory,
    staleTime: 0,
    refetchOnMount: "always"
  });
}
function DoseLotReferenceRow(t0) {
  const $ = _c(22);
  const {
    code,
    selected,
    onToggle,
    history,
    medicationName
  } = t0;
  const id = useId();
  const t1 = `Select lot ${code}`;
  let t2;
  let t3;
  if ($[0] !== code || $[1] !== onToggle) {
    t2 = () => onToggle(code);
    t3 = event => {
      if (event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      onToggle(code);
    };
    $[0] = code;
    $[1] = onToggle;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== id || $[5] !== selected || $[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <Checkbox id={id} checked={selected} data-lot-picker-choice={true} aria-label={t1} className="mt-0.5 border-border bg-background" onCheckedChange={t2} onKeyDown={t3} />;
    $[4] = id;
    $[5] = selected;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = history?.medicationName ?? medicationName;
  let t6;
  if ($[10] !== code || $[11] !== t5) {
    t6 = <LotRowLabel lotNumber={code} productName={t5} wrap={true} />;
    $[10] = code;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== history) {
    t7 = history && <span className="block text-caption text-foreground-secondary">{getLotHistoryDescription(history)}</span>;
    $[13] = history;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== id || $[16] !== t6 || $[17] !== t7) {
    t8 = <Label htmlFor={id} className="min-w-0 flex-1 break-words font-normal">{t6}{t7}</Label>;
    $[15] = id;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t4 || $[20] !== t8) {
    t9 = <div className="border-b border-border py-2 last:border-b-0"><div className="flex items-start gap-3 px-2 py-1">{t4}{t8}</div></div>;
    $[19] = t4;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
export function DoseLotReferenceRows(t0) {
  const $ = _c(11);
  const {
    choices,
    onToggle,
    history,
    medicationName
  } = t0;
  let t1;
  if ($[0] !== choices || $[1] !== history || $[2] !== medicationName || $[3] !== onToggle) {
    let t2;
    if ($[5] !== history || $[6] !== medicationName || $[7] !== onToggle) {
      t2 = choice => <DoseLotReferenceRow key={choice.code.toLowerCase()} {...choice} onToggle={onToggle} history={history.find(entry => entry.lotNumber.toLowerCase() === choice.code.toLowerCase())} medicationName={medicationName} />;
      $[5] = history;
      $[6] = medicationName;
      $[7] = onToggle;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = choices.map(t2);
    $[0] = choices;
    $[1] = history;
    $[2] = medicationName;
    $[3] = onToggle;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[9] !== t1) {
    t2 = <>{t1}</>;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}

/** Uses the inventory search; custom codes never cover physical stock quantities. */
export function DoseLotReferenceOptions(t0) {
  const $ = _c(43);
  const {
    medicationId,
    medicationName,
    shipments,
    search,
    references,
    onAdd,
    onSelectShipment,
    isLoadingInventory: t1,
    onPendingChange,
    historyQuery
  } = t0;
  const isLoadingInventory = t1 === undefined ? false : t1;
  const [notice, setNotice] = useState();
  const [entryActive, setEntryActive] = useState(false);
  const [entryKey, setEntryKey] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  let t2;
  if ($[0] !== onPendingChange) {
    t2 = () => {
      setEntryActive(false);
      onPendingChange(false);
    };
    $[0] = onPendingChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const finishEntry = t2;
  const {
    data: t3,
    isPending,
    isError,
    refetch
  } = historyQuery;
  let add;
  let t4;
  let t5;
  let t6;
  if ($[2] !== historyOpen || $[3] !== medicationName || $[4] !== onAdd || $[5] !== onSelectShipment || $[6] !== references || $[7] !== search || $[8] !== shipments || $[9] !== t3) {
    const history = t3 === undefined ? [] : t3;
    let t7;
    if ($[14] !== onAdd || $[15] !== onSelectShipment || $[16] !== shipments) {
      t7 = raw => {
        const code = raw.trim();
        if (!code) {
          return;
        }
        setNotice(undefined);
        const matches = shipments.filter(lot => lot.lotNumber?.trim().toLowerCase() === code.toLowerCase());
        if (matches.length > 1) {
          setNotice("Multiple inventory entries have this lot number. Use the supplier, location, and expiry to select the correct lot above.");
          return;
        }
        if (matches[0]) {
          onSelectShipment(matches[0].id);
          return;
        }
        onAdd(code);
      };
      $[14] = onAdd;
      $[15] = onSelectShipment;
      $[16] = shipments;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    add = t7;
    const visible = Array.from(new Map(history.map(_temp2)).values()).filter(entry_0 => entry_0.lotNumber.toLowerCase().includes(search.trim().toLowerCase()) && !references.some(value => value.toLowerCase() === entry_0.lotNumber.toLowerCase()) && !shipments.some(lot_0 => lot_0.lotNumber?.trim().toLowerCase() === entry_0.lotNumber.trim().toLowerCase()));
    t4 = "Lot history and new lot numbers";
    t5 = "space-y-2";
    t6 = visible.length > 0 && <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}><h3 className="text-caption-strong"><CollapsibleTrigger asChild={true}><button type="button" className="flex items-center gap-2 rounded outline-none focus-visible:ring-2 focus-visible:ring-primary"><FaChevronDown aria-hidden="true" className={cx("size-3 shrink-0 text-foreground-muted transition-transform duration-150", !historyOpen && "-rotate-90")} />Previously used lot numbers ({visible.length})</button></CollapsibleTrigger></h3><CollapsibleContent><div className="max-h-32 overflow-y-auto pt-2">{visible.map(entry_1 => <Button key={entry_1.lotNumber} type="button" variant="ghost" className="h-auto min-w-0 w-full flex-col items-start whitespace-normal text-left" onClick={() => add(entry_1.lotNumber)}><LotRowLabel lotNumber={entry_1.lotNumber} productName={entry_1.medicationName ?? medicationName} wrap={true} /><span className="w-full whitespace-normal break-words text-caption text-foreground-secondary">{getLotHistoryDescription(entry_1)}</span></Button>)}</div></CollapsibleContent></Collapsible>;
    $[2] = historyOpen;
    $[3] = medicationName;
    $[4] = onAdd;
    $[5] = onSelectShipment;
    $[6] = references;
    $[7] = search;
    $[8] = shipments;
    $[9] = t3;
    $[10] = add;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    add = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
  }
  let t7;
  if ($[18] !== isLoadingInventory || $[19] !== isPending || $[20] !== medicationId) {
    t7 = medicationId && isPending && !isLoadingInventory && <p className="text-caption text-foreground-secondary">Loading previously used lot numbers…</p>;
    $[18] = isLoadingInventory;
    $[19] = isPending;
    $[20] = medicationId;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== isError || $[23] !== refetch) {
    t8 = isError && <p role="alert" className="text-caption text-status-warning-text">Could not load lot history.{" "}<Button type="button" variant="ghost" size="sm" onClick={() => void refetch()}>Retry</Button></p>;
    $[22] = isError;
    $[23] = refetch;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== add || $[26] !== entryActive || $[27] !== entryKey || $[28] !== finishEntry || $[29] !== isLoadingInventory || $[30] !== onPendingChange || $[31] !== search) {
    t9 = !isLoadingInventory && <div data-dose-lot-new-entry={true} onKeyDownCapture={event => {
      if (event.key === "Escape") {
        finishEntry();
      }
    }}><DynamicSelectItem key={entryKey} label="Add new lot number" addPlaceholder="Enter new lot number..." initialValue={search} onInputChange={() => {
        setEntryActive(true);
        onPendingChange(true);
      }} onClose={finishEntry} onAddOption={value_0 => {
        add(value_0);
        finishEntry();
      }} />{entryActive && <div className="flex flex-wrap items-center justify-between gap-2"><p className="text-caption text-foreground-secondary">Add this lot or cancel the entry before saving.</p><Button type="button" variant="ghost" size="sm" onClick={() => {
          finishEntry();
          setEntryKey(_temp3);
        }}>Cancel new lot</Button></div>}</div>;
    $[25] = add;
    $[26] = entryActive;
    $[27] = entryKey;
    $[28] = finishEntry;
    $[29] = isLoadingInventory;
    $[30] = onPendingChange;
    $[31] = search;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== notice) {
    t10 = notice && <p role="status" className="text-caption text-status-warning-text">{notice}</p>;
    $[33] = notice;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t4 || $[37] !== t5 || $[38] !== t6 || $[39] !== t7 || $[40] !== t8 || $[41] !== t9) {
    t11 = <section aria-label={t4} className={t5}>{t6}{t7}{t8}{t9}{t10}</section>;
    $[35] = t10;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  return t11;
}
function _temp3(previous) {
  return previous + 1;
}
function _temp2(entry) {
  return [entry.lotNumber.toLowerCase(), entry];
}